import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { RiDoubleQuotesL } from 'react-icons/ri';
import { userApi } from '../../api/userApi';

const About = () => {
  const [images, setImages] = useState({
    bannerImage: '',
    subImage1: '',
    subImage2: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAboutImages = async () => {
      try {
        setIsLoading(true);
        const response = await userApi.getAboutImages();
        setImages({
          bannerImage: response.banner_image,
          subImage1: response.sub_image_1,
          subImage2: response.sub_image_2
        });
      } catch (error) {
        console.error('Failed to fetch about images:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAboutImages();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${images.bannerImage}')`
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-light mb-6">Our Story</h1>
            <div className="w-24 h-[2px] bg-white mx-auto"></div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        {/* Heritage Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-32"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-light">Our Heritage</h2>
              <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent"></div>
              <p className="text-gray-600 leading-relaxed">
                Founded in the heart of India's textile heritage, Panchali Design Hub emerged from a profound appreciation for traditional craftsmanship and a vision to bridge the gap between timeless artistry and contemporary fashion. Our journey began with a simple yet powerful idea: to preserve and celebrate India's rich textile heritage while creating modern, sustainable luxury.
              </p>
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-lg">
              <img 
                src={images.subImage1}
                alt="Panchali Heritage"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </motion.section>

        {/* Mission Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-32"
        >
          <div className="text-center max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl font-light">Our Mission</h2>
            <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent mx-auto"></div>
            <p className="text-gray-600 leading-relaxed">
              At Panchali Design Hub, we envision a future where luxury fashion harmoniously coexists with ethical practices and sustainable craftsmanship. Our commitment extends beyond creating beautiful garments – we're dedicated to preserving traditional techniques, supporting local artisans, and promoting sustainable fashion practices.
            </p>
          </div>
        </motion.section>

        {/* Craftsmanship Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-32"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] overflow-hidden rounded-lg">
              <img 
                src={images.subImage2}
                alt="Panchali Craftsmanship"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-light">Our Craftsmanship</h2>
              <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent"></div>
              <p className="text-gray-600 leading-relaxed">
                Each Panchali creation is a testament to meticulous craftsmanship and superior quality. We source the finest materials from sustainable producers and work with skilled artisans who bring generations of expertise to every piece. Our designs blend traditional techniques with contemporary aesthetics, creating garments that are both timeless and modern.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Sustainable Fashion Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-32"
        >
          <div className="text-center max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl font-light">Sustainable Fashion</h2>
            <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent mx-auto"></div>
            <p className="text-gray-600 leading-relaxed">
              Our commitment to sustainability goes beyond just using eco-friendly materials. We ensure fair wages for our artisans and maintain ethical working conditions throughout our supply chain.
            </p>
          </div>
        </motion.section>

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-8"
        >
          <h2 className="text-3xl font-light">Experience Luxury</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/products"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-900 hover:bg-indigo-800 transition-colors"
            >
              Explore Collections
              <FiArrowRight className="ml-2" />
            </Link>
            <Link 
              to="/contact"
              className="inline-flex items-center px-8 py-3 border border-indigo-900 text-base font-medium rounded-md text-indigo-900 hover:bg-indigo-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [dragStart, setDragStart] = useState(0);
  
  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await userApi.getAboutContent();
        if (response && response.length > 0) {
          setTestimonials(response);
        }
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      }
    };

    fetchTestimonials();
  }, []);

  // Auto-slide effect - Always declare this hook, but only run the interval if we have testimonials
  useEffect(() => {
    if (testimonials.length === 0) return;

    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleDragEnd = (event, info) => {
    const dragDistance = info.offset.x;
    const dragThreshold = 50; // minimum drag distance to trigger slide

    if (Math.abs(dragDistance) > dragThreshold) {
      if (dragDistance > 0) {
        // Dragged right - show previous
        setDirection(-1);
        setCurrentIndex(prev => 
          prev === 0 ? testimonials.length - 1 : prev - 1
        );
      } else {
        // Dragged left - show next
        setDirection(1);
        setCurrentIndex(prev => 
          prev === testimonials.length - 1 ? 0 : prev + 1
        );
      }
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  // If no testimonials, render nothing
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <motion.section 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-32"
    >
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h2 className="text-3xl font-light">What Our Customers Say</h2>
        <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent mx-auto mt-6"></div>
      </div>

      {/* Testimonial Carousel */}
      <div className="relative h-[400px] sm:h-[450px] md:h-[400px] flex items-center px-4 sm:px-8 md:px-12 overflow-x-hidden hide-scrollbar touch-pan-y">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="absolute w-full flex items-center justify-center px-4 sm:px-8 md:px-0 cursor-grab active:cursor-grabbing"
          >
            <div className="bg-white p-6 sm:p-8 md:p-12 shadow-xl rounded-lg w-full max-w-[90%] sm:max-w-[85%] md:max-w-[80%]">
              <RiDoubleQuotesL className="text-3xl sm:text-4xl text-indigo-900/20 mb-4 sm:mb-6" />
              <p className="text-gray-600 italic text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed">
                {testimonials[currentIndex].description}
              </p>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-lg sm:text-xl font-medium text-indigo-900">
                    {testimonials[currentIndex].name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">
                    {testimonials[currentIndex].name}
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm">
                    {testimonials[currentIndex].place}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-6 sm:w-8 bg-indigo-900' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </motion.section>
  );
};

export default About;




























